<template>
  <div class="dns-page py-3">
    <header class="dns-toolbar">
      <div class="dns-toolbar__title">
        <span class="dns-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>DNS · {{ domain }}</h1>
          <p v-if="managedExternally">
            DNS for this domain is hosted by a third party — see below for the active nameservers.
          </p>
          <p v-else>
            Every save replaces all host records with the rows below in a single Namecheap call.
            <span v-if="!isUsingOurDns" class="dns-warn">
              · This domain is <strong>not using Namecheap nameservers</strong>; edits here will have no effect until you switch back.
            </span>
          </p>
        </div>
      </div>
      <div class="dns-toolbar__actions">
        <router-link :to="{ name: 'SettingsDomains' }" class="dns-link-btn">← All domains</router-link>
        <CButton
          size="sm"
          class="refresh-btn"
          :class="{ 'is-loading': isLoading }"
          :disabled="isLoading || isSaving"
          @click="loadHosts"
        >{{ isLoading ? 'Refreshing…' : 'Refresh' }}</CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="dns-alert">{{ errorMessage }}</CAlert>
    <CAlert v-if="successMessage" color="success" show class="dns-alert">{{ successMessage }}</CAlert>

    <section v-if="managedExternally" class="dns-external">
      <div class="dns-external__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <path d="M2 12h20"/>
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
        </svg>
      </div>
      <div class="dns-external__body">
        <h2>DNS for this domain is managed elsewhere</h2>
        <p>
          <strong>{{ domain }}</strong> is pointed at custom nameservers, so its DNS records
          aren't editable from here. To change records, log into whoever runs these nameservers
          (often Cloudflare, Route&nbsp;53, or a hosting provider).
        </p>
        <div v-if="nameservers.length" class="dns-external__ns">
          <span class="dns-external__ns-label">Current nameservers</span>
          <ul>
            <li v-for="ns in nameservers" :key="ns">{{ ns }}</li>
          </ul>
          <p v-if="guessedProvider" class="dns-external__hint">
            Likely managed at <strong>{{ guessedProvider }}</strong>.
          </p>
        </div>
      </div>
    </section>

    <section v-else class="dns-section">
      <div class="dns-section__head">
        <h2>Host records</h2>
        <div class="dns-section__actions">
          <button type="button" class="dns-btn dns-btn--ghost" :disabled="isSaving" @click="resetDraft">Reset</button>
          <button type="button" class="dns-btn dns-btn--ghost" :disabled="isSaving" @click="addRow">+ Add record</button>
          <button
            type="button"
            class="dns-btn dns-btn--primary"
            :disabled="isSaving || !isDirty || !isDraftValid"
            @click="openConfirm"
          >{{ isDirty ? `Save (${draft.length} records)` : 'No changes' }}</button>
        </div>
      </div>

      <div v-if="isLoading" class="dns-state">Loading DNS records…</div>
      <div v-else-if="!draft.length" class="dns-state">
        No records yet.
        <button type="button" class="dns-linklike" @click="addRow">Add the first one</button>
      </div>

      <div v-else class="dns-records">
        <div class="dns-records__head">
          <span>Type</span>
          <span>Host</span>
          <span>Value / Target</span>
          <span>TTL</span>
          <span>MX&nbsp;Pref</span>
          <span aria-hidden="true"></span>
        </div>
        <div
          v-for="(rec, idx) in draft"
          :key="`rec-${idx}`"
          class="dns-records__row"
          :class="{ 'is-new': rec._new, 'is-invalid': !isRowValid(rec) }"
        >
          <select class="dns-input" v-model="rec.type" :disabled="isSaving">
            <option v-for="t in recordTypes" :key="t" :value="t">{{ t }}</option>
          </select>
          <input
            type="text"
            class="dns-input"
            v-model="rec.name"
            placeholder="@ or sub"
            :disabled="isSaving"
            maxlength="255"
          />
          <input
            type="text"
            class="dns-input"
            v-model="rec.address"
            :placeholder="addressPlaceholder(rec.type)"
            :disabled="isSaving"
            maxlength="2048"
          />
          <input
            type="number"
            class="dns-input dns-input--narrow"
            v-model.number="rec.ttl"
            min="60"
            max="60000"
            :disabled="isSaving"
          />
          <input
            type="number"
            class="dns-input dns-input--narrow"
            v-model.number="rec.mx_pref"
            min="0"
            max="65535"
            :disabled="isSaving || rec.type !== 'MX'"
            :placeholder="rec.type === 'MX' ? '10' : '—'"
          />
          <button
            type="button"
            class="dns-row-remove"
            :disabled="isSaving"
            @click="removeRow(idx)"
            aria-label="Remove row"
          >×</button>
        </div>
      </div>
    </section>

    <CModal
      :show.sync="showConfirm"
      size="md"
      :centered="true"
      title="Confirm DNS replacement"
      class="dns-modal"
      :close-on-backdrop="!isSaving"
    >
      <div class="dns-modal__body">
        <p class="dns-modal__lead">
          You're about to replace <strong>all {{ original.length }}</strong> host record{{ original.length === 1 ? '' : 's' }}
          on <strong>{{ domain }}</strong> with <strong>{{ draft.length }}</strong> record{{ draft.length === 1 ? '' : 's' }}.
          Namecheap has no per-record API — every row gets rewritten in one call.
        </p>

        <div v-if="diff.added.length || diff.removed.length || diff.changed.length" class="dns-diff">
          <div v-if="diff.added.length" class="dns-diff-group">
            <h4 class="dns-diff-title dns-diff-title--add">+ Adding ({{ diff.added.length }})</h4>
            <div v-for="(r, i) in diff.added" :key="`a-${i}`" class="dns-diff-row dns-diff-row--add">
              <span class="dns-diff-type">{{ r.type }}</span>
              <span class="dns-diff-host">{{ r.name || '@' }}</span>
              <span class="dns-diff-val">{{ r.address }}</span>
            </div>
          </div>
          <div v-if="diff.removed.length" class="dns-diff-group">
            <h4 class="dns-diff-title dns-diff-title--remove">− Removing ({{ diff.removed.length }})</h4>
            <div v-for="(r, i) in diff.removed" :key="`r-${i}`" class="dns-diff-row dns-diff-row--remove">
              <span class="dns-diff-type">{{ r.type }}</span>
              <span class="dns-diff-host">{{ r.name || '@' }}</span>
              <span class="dns-diff-val">{{ r.address }}</span>
            </div>
          </div>
          <div v-if="diff.changed.length" class="dns-diff-group">
            <h4 class="dns-diff-title dns-diff-title--change">~ Updating ({{ diff.changed.length }})</h4>
            <div v-for="(c, i) in diff.changed" :key="`c-${i}`" class="dns-diff-row dns-diff-row--change">
              <span class="dns-diff-type">{{ c.next.type }}</span>
              <span class="dns-diff-host">{{ c.next.name || '@' }}</span>
              <span class="dns-diff-val">
                <span class="dns-diff-old">{{ c.prev.address }}</span>
                <span class="dns-diff-arrow">→</span>
                <span class="dns-diff-new">{{ c.next.address }}</span>
              </span>
            </div>
          </div>
        </div>
        <div v-else class="dns-diff-empty">No structural differences detected — the record set is identical to the server. Saving will still write the same set.</div>
      </div>
      <template #footer>
        <button type="button" class="dns-btn dns-btn--ghost" :disabled="isSaving" @click="showConfirm = false">Cancel</button>
        <button
          type="button"
          class="dns-btn dns-btn--danger"
          :disabled="isSaving"
          @click="saveHosts"
        >{{ isSaving ? 'Saving…' : 'Replace all records' }}</button>
      </template>
    </CModal>
  </div>
</template>

<script>
import { domainsApi } from '@/api/domains.js'

const RECORD_TYPES = ['A', 'AAAA', 'CNAME', 'MX', 'MXE', 'TXT', 'URL', 'URL301', 'FRAME', 'NS', 'ALIAS', 'CAA']
const DEFAULT_TTL = 1800

function blankRow () {
  return { name: '@', type: 'A', address: '', ttl: DEFAULT_TTL, mx_pref: 10, _new: true }
}

function recordKey (r) {
  return `${(r.name || '@').toLowerCase()}|${(r.type || '').toUpperCase()}`
}

function recordsEqual (a, b) {
  return (a.name || '@') === (b.name || '@')
    && (a.type || '').toUpperCase() === (b.type || '').toUpperCase()
    && (a.address || '') === (b.address || '')
    && Number(a.ttl || DEFAULT_TTL) === Number(b.ttl || DEFAULT_TTL)
    && Number(a.mx_pref || 10) === Number(b.mx_pref || 10)
}

export default {
  name: 'SettingsDomainDns',
  data () {
    return {
      original: [],
      draft: [],
      isUsingOurDns: true,
      managedExternally: false,
      nameservers: [],
      isLoading: false,
      isSaving: false,
      errorMessage: '',
      successMessage: '',
      showConfirm: false,
      recordTypes: RECORD_TYPES
    }
  },
  computed: {
    domain () { return this.$route.params.domain },
    isDirty () {
      if (this.original.length !== this.draft.length) return true
      for (let i = 0; i < this.draft.length; i++) {
        if (!recordsEqual(this.draft[i], this.original[i])) return true
      }
      return false
    },
    isDraftValid () {
      return this.draft.every(this.isRowValid)
    },
    guessedProvider () {
      const ns = (this.nameservers || []).join(' ').toLowerCase()
      if (!ns) return ''
      if (ns.includes('cloudflare')) return 'Cloudflare'
      if (ns.includes('awsdns')) return 'AWS Route 53'
      if (ns.includes('googledomains') || ns.includes('google.com')) return 'Google Domains / Cloud DNS'
      if (ns.includes('digitalocean')) return 'DigitalOcean'
      if (ns.includes('hetzner')) return 'Hetzner'
      if (ns.includes('hostinger')) return 'Hostinger'
      if (ns.includes('godaddy') || ns.includes('domaincontrol')) return 'GoDaddy'
      if (ns.includes('vercel-dns')) return 'Vercel'
      if (ns.includes('netlify')) return 'Netlify'
      if (ns.includes('azure-dns')) return 'Azure DNS'
      if (ns.includes('dnsimple')) return 'DNSimple'
      if (ns.includes('name.com')) return 'Name.com'
      return ''
    },
    diff () {
      // Match draft↔original by (host,type). Rows that exist in both
      // with a different address/ttl/mx-pref are "changed"; rest are
      // additions or removals. This is just for the confirm modal — the
      // server still replaces everything, regardless of what we mark.
      const added = []
      const removed = []
      const changed = []
      const used = new Set()
      this.draft.forEach((next) => {
        const key = recordKey(next)
        const prevIdx = this.original.findIndex((o, i) => !used.has(i) && recordKey(o) === key)
        if (prevIdx === -1) {
          added.push(next)
        } else {
          used.add(prevIdx)
          const prev = this.original[prevIdx]
          if (!recordsEqual(prev, next)) changed.push({ prev, next })
        }
      })
      this.original.forEach((prev, i) => {
        if (!used.has(i)) removed.push(prev)
      })
      return { added, removed, changed }
    }
  },
  created () {
    this.loadHosts()
  },
  methods: {
    loadHosts () {
      this.isLoading = true
      this.errorMessage = ''
      domainsApi.getDns(this.domain)
        .then((response) => {
          const recs = Array.isArray(response.records) ? response.records : []
          this.original = recs.map((r) => this.normalizeServerRecord(r))
          this.draft = this.original.map((r) => ({ ...r }))
          this.isUsingOurDns = !!response.is_using_our_dns
          this.managedExternally = !!response.managed_externally
          this.nameservers = Array.isArray(response.nameservers) ? response.nameservers : []
        })
        .catch((error) => {
          this.errorMessage = error?.response?.data?.message || error.message || 'Unable to load DNS records.'
        })
        .finally(() => { this.isLoading = false })
    },
    normalizeServerRecord (r) {
      return {
        name: r.name || '@',
        type: (r.type || 'A').toUpperCase(),
        address: r.address || '',
        ttl: Number(r.ttl) || DEFAULT_TTL,
        mx_pref: Number(r.mx_pref) || 10,
        _new: false
      }
    },
    addRow () {
      this.draft.push(blankRow())
    },
    removeRow (idx) {
      this.draft.splice(idx, 1)
    },
    resetDraft () {
      this.draft = this.original.map((r) => ({ ...r }))
    },
    isRowValid (r) {
      if (!r) return false
      const name = (r.name || '').trim()
      const address = (r.address || '').trim()
      if (!name) return false
      if (!address) return false
      if (!RECORD_TYPES.includes(r.type)) return false
      const ttl = Number(r.ttl)
      if (!Number.isFinite(ttl) || ttl < 60 || ttl > 60000) return false
      if (r.type === 'MX') {
        const pref = Number(r.mx_pref)
        if (!Number.isFinite(pref) || pref < 0 || pref > 65535) return false
      }
      return true
    },
    addressPlaceholder (type) {
      switch (type) {
        case 'A':      return 'IPv4 address (1.2.3.4)'
        case 'AAAA':   return 'IPv6 address'
        case 'CNAME':  return 'Target hostname (example.com.)'
        case 'MX':     return 'Mail server hostname'
        case 'TXT':    return 'Free-form text (max 2048)'
        case 'NS':     return 'Nameserver hostname'
        case 'URL':
        case 'URL301':
        case 'FRAME':  return 'https://example.com/…'
        case 'CAA':    return '0 issue "letsencrypt.org"'
        default:       return 'Record value'
      }
    },
    openConfirm () {
      if (!this.isDraftValid) {
        this.errorMessage = 'Fix invalid rows before saving (highlighted in red).'
        return
      }
      this.errorMessage = ''
      this.showConfirm = true
    },
    saveHosts () {
      this.isSaving = true
      const payload = this.draft.map((r) => ({
        name: (r.name || '@').trim(),
        type: r.type,
        address: (r.address || '').trim(),
        ttl: Number(r.ttl) || DEFAULT_TTL,
        mx_pref: r.type === 'MX' ? (Number(r.mx_pref) || 10) : 10
      }))
      domainsApi.setDns(this.domain, payload)
        .then((response) => {
          const recs = Array.isArray(response.records) ? response.records : []
          this.original = recs.map((r) => this.normalizeServerRecord(r))
          this.draft = this.original.map((r) => ({ ...r }))
          this.isUsingOurDns = !!response.is_using_our_dns
          this.successMessage = `DNS for ${this.domain} updated. ${recs.length} record${recs.length === 1 ? '' : 's'} live.`
          this.showConfirm = false
          setTimeout(() => { this.successMessage = '' }, 5000)
        })
        .catch((error) => {
          this.errorMessage = error?.response?.data?.message || error.message || 'Failed to update DNS.'
        })
        .finally(() => { this.isSaving = false })
    }
  }
}
</script>

<style scoped>
.dns-page { padding: 0.25rem 0; }

.dns-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
  flex-wrap: wrap;
}

.dns-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.dns-toolbar__accent {
  width: 4px;
  height: 36px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}

.dns-toolbar h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.dns-toolbar p {
  margin: 0;
  font-size: 0.78rem;
  color: #6b7280;
  max-width: 60ch;
}

.dns-warn { color: #b45309; }

.dns-toolbar__actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
}

.dns-link-btn {
  font-size: 0.82rem;
  color: #0e5b46;
  text-decoration: none;
  font-weight: 600;
}

.dns-link-btn:hover { text-decoration: underline; }

.dns-toolbar__actions >>> .btn.refresh-btn {
  height: 34px;
  padding: 0 0.85rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
}

.dns-toolbar__actions >>> .btn.refresh-btn:focus { outline: none; }
.dns-toolbar__actions >>> .btn.refresh-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.dns-alert { margin: 0 0 0.75rem; border-radius: 10px; }

.dns-external {
  display: flex;
  gap: 0.85rem;
  align-items: flex-start;
  padding: 1rem 1.1rem;
  background: #fbfcfe;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
}

.dns-external__icon {
  flex: 0 0 38px;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #ecfdf5;
  color: #0e5b46;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.dns-external__body { flex: 1; min-width: 0; }

.dns-external__body h2 {
  margin: 0 0 0.3rem;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
}

.dns-external__body p {
  margin: 0 0 0.65rem;
  font-size: 0.82rem;
  color: #4b5563;
  line-height: 1.5;
  max-width: 70ch;
}

.dns-external__ns {
  background: #ffffff;
  border: 1px solid #eef1f7;
  border-radius: 8px;
  padding: 0.65rem 0.85rem;
}

.dns-external__ns-label {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
}

.dns-external__ns ul {
  margin: 0.35rem 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.18rem;
}

.dns-external__ns li {
  font-family: ui-monospace, Menlo, Consolas, monospace;
  font-size: 0.82rem;
  color: #1f2a44;
}

.dns-external__hint {
  margin: 0.55rem 0 0;
  font-size: 0.78rem;
  color: #0e5b46;
}

.dns-section {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
}

.dns-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
  flex-wrap: wrap;
}

.dns-section__head h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
}

.dns-section__actions {
  display: flex;
  gap: 0.45rem;
  flex-wrap: wrap;
}

.dns-state {
  padding: 1.4rem 1rem;
  text-align: center;
  color: #6b7280;
  font-size: 0.85rem;
}

.dns-linklike {
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  color: #0e5b46;
  font-weight: 600;
  cursor: pointer;
}

.dns-linklike:hover { text-decoration: underline; }

.dns-records { padding: 0.5rem 0.5rem 1rem; overflow-x: auto; }

.dns-records__head,
.dns-records__row {
  display: grid;
  grid-template-columns: 110px 1fr 2fr 90px 90px 32px;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.6rem;
  min-width: 720px;
}

.dns-records__head {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
  background: #f8fafc;
  border: 1px solid #eef1f7;
  border-radius: 8px 8px 0 0;
  border-bottom: 0;
}

.dns-records__row {
  background: #ffffff;
  border: 1px solid #eef1f7;
  border-top: 0;
}

.dns-records__row.is-new { background: #f5fbf8; }

.dns-records__row.is-invalid {
  background: #fef2f2;
  border-color: #fecaca;
}

.dns-records__row + .dns-records__row { border-top: 0; }

.dns-records__row:last-child { border-radius: 0 0 8px 8px; }

.dns-input {
  height: 32px;
  padding: 0 0.55rem;
  border: 1px solid #d8dde6;
  border-radius: 6px;
  background: #ffffff;
  font-family: inherit;
  font-size: 0.82rem;
  color: #1f2a44;
  width: 100%;
  min-width: 0;
}

.dns-input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.dns-input:disabled { background: #f8fafc; color: #6b7280; cursor: not-allowed; }

.dns-input--narrow { text-align: right; }

.dns-row-remove {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: 1px solid #e5e9f2;
  background: #ffffff;
  color: #6b7280;
  font-size: 1.05rem;
  line-height: 1;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
}

.dns-row-remove:hover:not(:disabled) {
  border-color: #ef4444;
  color: #b91c1c;
  background: #fef2f2;
}

.dns-row-remove:focus { outline: none; }
.dns-row-remove:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.30);
}

.dns-row-remove:disabled { opacity: 0.4; cursor: not-allowed; }

.dns-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 34px;
  min-width: 90px;
  padding: 0.45rem 1.1rem;
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease, background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.dns-btn:focus { outline: none; }
.dns-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.dns-btn:disabled { cursor: not-allowed; opacity: 0.55; filter: none; transform: none; box-shadow: none; }

.dns-btn--ghost {
  background: #ffffff;
  border-color: #e5e9f2;
  color: #1f2a44;
}

.dns-btn--ghost:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #d8dde6;
}

.dns-btn--primary {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border-color: transparent;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
}

.dns-btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}

.dns-btn--danger {
  background: #b91c1c;
  border-color: transparent;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(185, 28, 28, 0.18);
}

.dns-btn--danger:hover:not(:disabled) {
  filter: brightness(1.05);
}

.dns-btn--danger:focus-visible {
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.30);
}

.dns-modal >>> .modal-content {
  border-radius: 14px;
  border: 1px solid #e5e9f2;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.22);
  overflow: hidden;
}

.dns-modal >>> .modal-header {
  border-bottom: 1px solid #eef1f7;
  background: #ffffff;
  padding: 0.95rem 1.15rem;
}

.dns-modal >>> .modal-header .modal-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1f2a44;
}

.dns-modal >>> .modal-body {
  background: #fbfcfe;
  padding: 1.1rem;
  max-height: calc(100vh - 220px);
  overflow-y: auto;
}

.dns-modal >>> .modal-footer {
  border-top: 1px solid #eef1f7;
  background: #ffffff;
  padding: 0.7rem 1.15rem;
  gap: 0.5rem;
}

.dns-modal__body {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.dns-modal__lead {
  margin: 0;
  font-size: 0.85rem;
  color: #1f2a44;
  line-height: 1.5;
}

.dns-diff {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.dns-diff-group {
  border: 1px solid #e5e9f2;
  border-radius: 8px;
  overflow: hidden;
}

.dns-diff-title {
  margin: 0;
  padding: 0.45rem 0.7rem;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.dns-diff-title--add    { background: #dff5ea; color: #0e5b46; }
.dns-diff-title--remove { background: #fee2e2; color: #991b1b; }
.dns-diff-title--change { background: #fef3c7; color: #92400e; }

.dns-diff-row {
  display: grid;
  grid-template-columns: 70px 130px 1fr;
  gap: 0.5rem;
  padding: 0.4rem 0.7rem;
  font-size: 0.8rem;
  border-top: 1px solid #f1f5f9;
  align-items: center;
}

.dns-diff-row--add    { background: #f5fbf8; }
.dns-diff-row--remove { background: #fef7f7; }
.dns-diff-row--change { background: #fffbf2; }

.dns-diff-type {
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: 0.02em;
}

.dns-diff-host {
  font-family: ui-monospace, Menlo, Consolas, monospace;
  color: #1f2a44;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dns-diff-val {
  font-family: ui-monospace, Menlo, Consolas, monospace;
  color: #4b5563;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-flex;
  gap: 0.4rem;
  align-items: center;
}

.dns-diff-old { color: #b91c1c; text-decoration: line-through; }
.dns-diff-new { color: #0e5b46; font-weight: 600; }
.dns-diff-arrow { color: #6b7280; }

.dns-diff-empty {
  padding: 0.7rem 0.9rem;
  background: #f8fafc;
  color: #4b5563;
  font-size: 0.82rem;
  border-radius: 8px;
  border: 1px dashed #cbd5e1;
}

@media (max-width: 640px) {
  .dns-records__head { display: none; }
  .dns-records__row {
    grid-template-columns: 1fr 1fr;
    min-width: 0;
  }
}
</style>
